import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
  PlayIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  ClockIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowsUpDownIcon,
  EyeIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { 
  getAutomationExecutions, 
  getExecutionTypes, 
  getExecutionRules,
  ExecutionFilters 
} from '../services/automationApi';
import { AutomationExecution } from '../types/Automation';
import { useBranch } from '../contexts/BranchContext';

interface ExecutionDetailModalProps {
  execution: AutomationExecution | null;
  isOpen: boolean;
  onClose: () => void;
}

const ExecutionDetailModal: React.FC<ExecutionDetailModalProps> = ({ execution, isOpen, onClose }) => {
  if (!isOpen || !execution) return null;

  const getStatusBadge = (status: string) => {
    const styles = {
      success: 'bg-green-100 text-green-800 border-green-200',
      failed: 'bg-red-100 text-red-800 border-red-200',
      partial: 'bg-yellow-100 text-yellow-800 border-yellow-200'
    };
    return styles[status as keyof typeof styles] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatExecutionType = (type: string) => {
    if (!type) return 'Unknown';
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />
        
        <div className="relative inline-block w-full max-w-3xl p-0 overflow-hidden text-left align-middle transition-all transform bg-white rounded-xl shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-purple-50">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Execution Details</h3>
                <p className="text-sm text-gray-500">ID: #{execution.id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {/* Status & Time Row */}
            <div className="flex items-center justify-between mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadge(execution.status || 'unknown')}`}>
                  {execution.status === 'success' && <CheckCircleIcon className="h-4 w-4 mr-1.5" />}
                  {execution.status === 'failed' && <XCircleIcon className="h-4 w-4 mr-1.5" />}
                  {execution.status === 'partial' && <ExclamationTriangleIcon className="h-4 w-4 mr-1.5" />}
                  {(execution.status || 'Unknown').charAt(0).toUpperCase() + (execution.status || 'unknown').slice(1)}
                </span>
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
                  {formatExecutionType(execution.executionType)}
                </span>
              </div>
              <div className="flex items-center text-sm text-gray-600">
                <ClockIcon className="h-4 w-4 mr-1.5" />
                {formatDateTime(execution.executedAt)}
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {/* Automation Rule */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Automation Rule</h4>
                <p className="text-base font-medium text-gray-900">
                  {execution.automationRuleName || `Rule #${execution.automationRuleId || 'N/A'}`}
                </p>
                {execution.automationRuleType && (
                  <p className="text-sm text-gray-500 mt-1">Type: {execution.automationRuleType}</p>
                )}
              </div>

              {/* Lead Info */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Lead Information</h4>
                <p className="text-base font-medium text-gray-900">
                  {execution.leadFirstName || ''} {execution.leadLastName || 'Unknown Lead'}
                </p>
                {execution.leadEmail && (
                  <p className="text-sm text-gray-500">{execution.leadEmail}</p>
                )}
                {execution.leadPhone && (
                  <p className="text-sm text-gray-500">{execution.leadPhone}</p>
                )}
                {execution.leadStageName && (
                  <div className="flex items-center mt-2">
                    <span 
                      className="inline-block w-2 h-2 rounded-full mr-2"
                      style={{ backgroundColor: execution.leadStageColor || '#6B7280' }}
                    />
                    <span className="text-sm text-gray-600">{execution.leadStageName}</span>
                  </div>
                )}
              </div>

              {/* Branch Info */}
              {execution.branchName && (
                <div className="p-4 border border-gray-200 rounded-lg">
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Branch</h4>
                  <p className="text-base font-medium text-gray-900">{execution.branchName}</p>
                </div>
              )}

              {/* Executed By */}
              <div className="p-4 border border-gray-200 rounded-lg">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Executed By</h4>
                <div className="flex items-center">
                  <div className="p-1.5 bg-gray-100 rounded-full mr-2">
                    <UserIcon className="h-4 w-4 text-gray-600" />
                  </div>
                  <p className="text-base font-medium text-gray-900">
                    {execution.executedByFirstName && execution.executedByLastName
                      ? `${execution.executedByFirstName} ${execution.executedByLastName}`
                      : 'System (Automated)'}
                  </p>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {execution.errorMessage && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-start">
                  <XCircleIcon className="h-5 w-5 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                  <div>
                    <h4 className="text-sm font-medium text-red-800">Error Message</h4>
                    <p className="text-sm text-red-700 mt-1">{execution.errorMessage}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Execution Data */}
            {execution.executionData && Object.keys(execution.executionData).length > 0 && (
              <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Execution Data</h4>
                <pre className="text-sm text-gray-700 bg-white p-3 rounded border border-gray-200 overflow-x-auto">
                  {JSON.stringify(execution.executionData, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface InternalPagination {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  limit: number;
}

const defaultPagination: InternalPagination = {
  currentPage: 1,
  totalPages: 1,
  totalRecords: 0,
  hasNextPage: false,
  hasPrevPage: false,
  limit: 20
};

const AutomationExecutions: React.FC = () => {
  const { currentBranch, isViewingAllBranches, loading: branchLoading } = useBranch();
  const [executions, setExecutions] = useState<AutomationExecution[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState<InternalPagination>(defaultPagination);

  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<ExecutionFilters>({
    page: 1,
    limit: 20,
    sortBy: 'executed_at',
    sortOrder: 'DESC'
  });
  const [searchInput, setSearchInput] = useState('');
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Filter options
  const [executionTypes, setExecutionTypes] = useState<string[]>([]);
  const [ruleOptions, setRuleOptions] = useState<{ id: number; name: string }[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  // Detail modal
  const [selectedExecution, setSelectedExecution] = useState<AutomationExecution | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // Expanded rows
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        setLoadingOptions(true);
        const [types, rules] = await Promise.all([
          getExecutionTypes(),
          getExecutionRules()
        ]);
        setExecutionTypes(types);
        setRuleOptions(rules);
      } catch (err) {
        console.error('Error loading filter options:', err);
      } finally {
        setLoadingOptions(false);
      }
    };

    loadFilterOptions();
  }, []);

  const loadExecutions = useCallback(async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);
      
      const response = await getAutomationExecutions(filters);
      setExecutions(response.executions || []);
      setPagination({
        currentPage: response.pagination?.currentPage || 1,
        totalPages: response.pagination?.totalPages || 1,
        totalRecords: response.pagination?.totalRecords || 0,
        hasNextPage: response.pagination?.hasNextPage || false,
        hasPrevPage: response.pagination?.hasPrevPage || false,
        limit: response.pagination?.limit || 20
      });
    } catch (err: any) {
      console.error('Error loading executions:', err);
      setError(err.message || 'Failed to load execution history');
      setExecutions([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [filters]);

  useEffect(() => {
    if (!branchLoading) {
      loadExecutions();
    }
  }, [loadExecutions, currentBranch, isViewingAllBranches, branchLoading]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      if (searchInput !== (filters.search || '')) {
        setFilters(prev => ({ ...prev, search: searchInput || undefined, page: 1 }));
      }
    }, 400);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  const handleFilterChange = (field: keyof ExecutionFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [field]: value || undefined,
      page: field !== 'page' ? 1 : value
    }));
  };

  const handleSort = (field: string) => {
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: prev.sortBy === field && prev.sortOrder === 'DESC' ? 'ASC' : 'DESC',
      page: 1
    }));
  };

  const clearFilters = () => {
    setSearchInput('');
    setFilters({
      page: 1,
      limit: 20,
      sortBy: 'executed_at',
      sortOrder: 'DESC'
    });
  };

  const hasActiveFilters = !!(
    filters.status || 
    filters.executionType || 
    filters.ruleId || 
    filters.dateFrom || 
    filters.dateTo || 
    filters.search
  );

  const toggleRowExpand = (id: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'partial':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <PlayIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      success: 'bg-green-50 text-green-700 ring-green-600/20',
      failed: 'bg-red-50 text-red-700 ring-red-600/20',
      partial: 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
    };
    return styles[status as keyof typeof styles] || 'bg-gray-50 text-gray-700 ring-gray-600/20';
  };

  const formatExecutionType = (type: string) => {
    if (!type) return 'Unknown';
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(' ');
  };

  const formatDateTime = (dateString: string) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Invalid Date';
    }
  };

  const formatRelativeTime = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;
      return '';
    } catch {
      return '';
    }
  };

  // Pagination range helper
  const getPaginationRange = () => {
    const totalPages = pagination.totalPages;
    const current = pagination.currentPage;
    const delta = 2;
    const range: (number | string)[] = [];
    
    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 ||
        i === totalPages ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      } else if (range[range.length - 1] !== '...') {
        range.push('...');
      }
    }
    
    return range;
  };

  if (loading && executions.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="flex items-center justify-between mb-6">
            <div className="h-8 bg-gray-200 rounded w-1/4"></div>
            <div className="h-10 bg-gray-200 rounded w-32"></div>
          </div>
          <div className="h-12 bg-gray-200 rounded mb-4"></div>
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Execution History</h2>
          <p className="text-sm text-gray-600 mt-1">
            Complete log of all automation executions in your organization
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => loadExecutions(true)}
            disabled={refreshing}
            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
          >
            <ArrowPathIcon className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-6">
        <div className="p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by rule name, lead name, or email..."
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
              />
              {searchInput && (
                <button
                  onClick={() => setSearchInput('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Quick Filters */}
            <div className="flex items-center gap-3">
              {/* Status Filter */}
              <select
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Status</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="partial">Partial</option>
              </select>

              {/* Toggle Advanced Filters */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`inline-flex items-center px-3 py-2.5 text-sm font-medium border rounded-lg transition-colors ${
                  showFilters || hasActiveFilters
                    ? 'bg-indigo-50 text-indigo-700 border-indigo-300'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
              >
                <FunnelIcon className="h-4 w-4 mr-2" />
                Filters
                {hasActiveFilters && (
                  <span className="ml-2 px-1.5 py-0.5 text-xs bg-indigo-600 text-white rounded-full">
                    {[filters.status, filters.executionType, filters.ruleId, filters.dateFrom, filters.dateTo, filters.search].filter(Boolean).length}
                  </span>
                )}
                {showFilters ? (
                  <ChevronUpIcon className="h-4 w-4 ml-2" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 ml-2" />
                )}
              </button>

              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center px-3 py-2.5 text-sm font-medium text-red-600 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4 mr-1" />
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Advanced Filters Panel */}
          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Execution Type */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Execution Type</label>
                  <select
                    value={filters.executionType || ''}
                    onChange={(e) => handleFilterChange('executionType', e.target.value)}
                    disabled={loadingOptions}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Types</option>
                    {executionTypes.map(type => (
                      <option key={type} value={type}>{formatExecutionType(type)}</option>
                    ))}
                  </select>
                </div>

                {/* Rule */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Automation Rule</label>
                  <select
                    value={filters.ruleId || ''}
                    onChange={(e) => handleFilterChange('ruleId', e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={loadingOptions}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">All Rules</option>
                    {ruleOptions.map(rule => (
                      <option key={rule.id} value={rule.id}>{rule.name}</option>
                    ))}
                  </select>
                </div>

                {/* Date From */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">From Date</label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="date"
                      value={filters.dateFrom || ''}
                      onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                {/* Date To */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">To Date</label>
                  <div className="relative">
                    <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="date"
                      value={filters.dateTo || ''}
                      onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                      className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Results Summary */}
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 rounded-b-xl flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {pagination.totalRecords > 0 ? (
              <>
                Showing <span className="font-medium">{((pagination.currentPage - 1) * pagination.limit) + 1}</span>
                {' '}-{' '}
                <span className="font-medium">{Math.min(pagination.currentPage * pagination.limit, pagination.totalRecords)}</span>
                {' '}of{' '}
                <span className="font-medium">{pagination.totalRecords.toLocaleString()}</span> executions
              </>
            ) : (
              'No executions found'
            )}
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm text-gray-600">Per page:</label>
            <select
              value={filters.limit || 20}
              onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex">
            <XCircleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error loading executions</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={() => loadExecutions()}
                className="mt-2 text-sm font-medium text-red-800 hover:text-red-900 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Executions Table */}
      {!executions || executions.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="text-center py-16">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
              <PlayIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900">No executions found</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-md mx-auto">
              {hasActiveFilters 
                ? 'No executions match your current filters. Try adjusting or clearing them.'
                : 'Automation executions will appear here once your rules start running.'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-4 inline-flex items-center px-4 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors"
              >
                Clear all filters
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {/* Table Header */}
          <div className="hidden lg:grid lg:grid-cols-12 gap-4 px-6 py-3 bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="col-span-1">Status</div>
            <div className="col-span-3">
              <button 
                onClick={() => handleSort('automation_rule_name')}
                className="flex items-center hover:text-gray-700"
              >
                Rule
                <ArrowsUpDownIcon className="h-3.5 w-3.5 ml-1" />
              </button>
            </div>
            <div className="col-span-2">Lead</div>
            <div className="col-span-2">Type</div>
            <div className="col-span-2">
              <button 
                onClick={() => handleSort('executed_at')}
                className="flex items-center hover:text-gray-700"
              >
                Executed
                <ArrowsUpDownIcon className="h-3.5 w-3.5 ml-1" />
              </button>
            </div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-gray-100">
            {executions.map((execution) => (
              <div key={execution.id}>
                {/* Main Row */}
                <div 
                  className={`px-6 py-4 hover:bg-gray-50 transition-colors cursor-pointer ${expandedRows.has(execution.id) ? 'bg-gray-50' : ''}`}
                  onClick={() => toggleRowExpand(execution.id)}
                >
                  <div className="lg:grid lg:grid-cols-12 lg:gap-4 lg:items-center">
                    {/* Status */}
                    <div className="col-span-1 mb-3 lg:mb-0">
                      <div className="flex items-center lg:justify-start">
                        {getStatusIcon(execution.status || 'unknown')}
                        <span className={`lg:hidden ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset ${getStatusBadge(execution.status || 'unknown')}`}>
                          {(execution.status || 'Unknown').charAt(0).toUpperCase() + (execution.status || 'unknown').slice(1)}
                        </span>
                      </div>
                    </div>

                    {/* Rule Name */}
                    <div className="col-span-3 mb-2 lg:mb-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {execution.automationRuleName || `Rule #${execution.automationRuleId || 'N/A'}`}
                      </p>
                      <p className="lg:hidden text-xs text-gray-500">
                        {execution.automationRuleType && (
                          <span className="capitalize">{execution.automationRuleType.replace('_', ' ')}</span>
                        )}
                      </p>
                    </div>

                    {/* Lead */}
                    <div className="col-span-2 mb-2 lg:mb-0">
                      <p className="text-sm text-gray-900 truncate">
                        {execution.leadFirstName || ''} {execution.leadLastName || 'Unknown'}
                      </p>
                      {execution.leadEmail && (
                        <p className="text-xs text-gray-500 truncate">{execution.leadEmail}</p>
                      )}
                    </div>

                    {/* Execution Type */}
                    <div className="col-span-2 mb-2 lg:mb-0">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-indigo-50 text-indigo-700">
                        {formatExecutionType(execution.executionType)}
                      </span>
                    </div>

                    {/* Executed At */}
                    <div className="col-span-2 mb-2 lg:mb-0">
                      <p className="text-sm text-gray-900">{formatDateTime(execution.executedAt)}</p>
                      {formatRelativeTime(execution.executedAt) && (
                        <p className="text-xs text-gray-500">{formatRelativeTime(execution.executedAt)}</p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="col-span-2 flex items-center justify-end space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedExecution(execution);
                          setShowDetailModal(true);
                        }}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleRowExpand(execution.id);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        title={expandedRows.has(execution.id) ? 'Collapse' : 'Expand'}
                      >
                        {expandedRows.has(execution.id) ? (
                          <ChevronUpIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {expandedRows.has(execution.id) && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      {/* Branch */}
                      {execution.branchName && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Branch</p>
                          <p className="text-sm text-gray-900">{execution.branchName}</p>
                        </div>
                      )}

                      {/* Lead Stage */}
                      {execution.leadStageName && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Lead Stage</p>
                          <div className="flex items-center">
                            <span 
                              className="inline-block w-2.5 h-2.5 rounded-full mr-2"
                              style={{ backgroundColor: execution.leadStageColor || '#6B7280' }}
                            />
                            <span className="text-sm text-gray-900">{execution.leadStageName}</span>
                          </div>
                        </div>
                      )}

                      {/* Executed By */}
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Executed By</p>
                        <div className="flex items-center">
                          <UserIcon className="h-4 w-4 text-gray-400 mr-1.5" />
                          <span className="text-sm text-gray-900">
                            {execution.executedByFirstName && execution.executedByLastName
                              ? `${execution.executedByFirstName} ${execution.executedByLastName}`
                              : 'System'}
                          </span>
                        </div>
                      </div>

                      {/* Lead Phone */}
                      {execution.leadPhone && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Lead Phone</p>
                          <p className="text-sm text-gray-900">{execution.leadPhone}</p>
                        </div>
                      )}
                    </div>

                    {/* Error Message */}
                    {execution.errorMessage && (
                      <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start">
                          <XCircleIcon className="h-4 w-4 text-red-500 mt-0.5 mr-2 flex-shrink-0" />
                          <div>
                            <p className="text-xs font-medium text-red-800">Error</p>
                            <p className="text-sm text-red-700 mt-0.5">{execution.errorMessage}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Execution Data Preview */}
                    {execution.executionData && Object.keys(execution.executionData).length > 0 && (
                      <div className="mt-4">
                        <details className="group">
                          <summary className="flex items-center cursor-pointer text-xs font-medium text-gray-500 uppercase tracking-wider hover:text-gray-700">
                            <InformationCircleIcon className="h-4 w-4 mr-1" />
                            Execution Data
                            <ChevronDownIcon className="h-3 w-3 ml-1 group-open:rotate-180 transition-transform" />
                          </summary>
                          <pre className="mt-2 p-3 bg-white border border-gray-200 rounded-lg text-xs text-gray-700 overflow-x-auto">
                            {JSON.stringify(execution.executionData, null, 2)}
                          </pre>
                        </details>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-600">
            Page <span className="font-medium">{pagination.currentPage}</span> of{' '}
            <span className="font-medium">{pagination.totalPages}</span>
          </div>
          
          <div className="flex items-center space-x-1">
            {/* First Page */}
            <button
              onClick={() => handleFilterChange('page', 1)}
              disabled={pagination.currentPage === 1}
              className="p-2 text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="First page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
              <ChevronLeftIcon className="h-4 w-4 -ml-2" />
            </button>

            {/* Previous */}
            <button
              onClick={() => handleFilterChange('page', pagination.currentPage - 1)}
              disabled={!pagination.hasPrevPage}
              className="p-2 text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Previous page"
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>

            {/* Page Numbers */}
            <div className="hidden sm:flex items-center space-x-1">
              {getPaginationRange().map((page, index) => (
                page === '...' ? (
                  <span key={`ellipsis-${index}`} className="px-3 py-2 text-gray-400">...</span>
                ) : (
                  <button
                    key={page}
                    onClick={() => handleFilterChange('page', page as number)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      pagination.currentPage === page
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                )
              ))}
            </div>

            {/* Mobile Page Input */}
            <div className="sm:hidden flex items-center space-x-2">
              <input
                type="number"
                min={1}
                max={pagination.totalPages}
                value={pagination.currentPage}
                onChange={(e) => {
                  const page = parseInt(e.target.value);
                  if (page >= 1 && page <= pagination.totalPages) {
                    handleFilterChange('page', page);
                  }
                }}
                className="w-16 px-2 py-2 text-center text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Next */}
            <button
              onClick={() => handleFilterChange('page', pagination.currentPage + 1)}
              disabled={!pagination.hasNextPage}
              className="p-2 text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Next page"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>

            {/* Last Page */}
            <button
              onClick={() => handleFilterChange('page', pagination.totalPages)}
              disabled={pagination.currentPage === pagination.totalPages}
              className="p-2 text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title="Last page"
            >
              <ChevronRightIcon className="h-4 w-4" />
              <ChevronRightIcon className="h-4 w-4 -ml-2" />
            </button>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      <ExecutionDetailModal
        execution={selectedExecution}
        isOpen={showDetailModal}
        onClose={() => {
          setShowDetailModal(false);
          setSelectedExecution(null);
        }}
      />
    </div>
  );
};

export default AutomationExecutions;
